import {
  ArrowRight,
  ExternalLink,
  Info,
  Lightbulb,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import type {
  Block,
  CalloutBlock,
  ImageBlock,
  LinkBlock,
  ListBlock,
  QuoteBlock,
  VideoBlock,
} from "../../schema/index.ts";
import { pickText, type CalloutTone, type Locale } from "../../schema/core.ts";
import { useSiteCopy } from "../i18n/useSiteCopy.ts";
import { cn } from "../ui/cn.ts";
import { QuizBlockView } from "./QuizBlockView.tsx";
import { InlineText, Paragraphs } from "./RichText.tsx";
import { safeHref, videoEmbedUrl } from "./urls.ts";

interface BlockRendererProps {
  blocks: Block[];
  locale: Locale;
}

/** Lesson body for learners (also the builder's live preview). Empty blocks render nothing. */
export const BlockRenderer = ({ blocks, locale }: BlockRendererProps): ReactNode => (
  <div className="ck-prose">
    {blocks.map((block) => (
      <BlockView key={block.id} block={block} locale={locale} />
    ))}
  </div>
);

const BlockView = ({ block, locale }: { block: Block; locale: Locale }): ReactNode => {
  switch (block.type) {
    case "heading": {
      const text = pickText(block.text, locale).trim();
      return text ? <h2>{text}</h2> : null;
    }
    case "paragraph": {
      const text = pickText(block.text, locale);
      return text.trim() ? (
        <div>
          <Paragraphs text={text} />
        </div>
      ) : null;
    }
    case "list":
      return <ListView block={block} locale={locale} />;
    case "image":
      return <ImageView block={block} locale={locale} />;
    case "callout":
      return <CalloutView block={block} locale={locale} />;
    case "quote":
      return <QuoteView block={block} locale={locale} />;
    case "video":
      return <VideoView block={block} locale={locale} />;
    case "link":
      return <LinkView block={block} locale={locale} />;
    case "quiz":
      return <QuizBlockView block={block} locale={locale} />;
  }
};

const ListView = ({ block, locale }: { block: ListBlock; locale: Locale }): ReactNode => {
  const items = block.items.map((item) => pickText(item, locale)).filter((item) => item.trim());
  if (items.length === 0) return null;
  const List = block.style === "number" ? "ol" : "ul";
  return (
    <List
      className={cn(
        "flex flex-col gap-2 pl-6 marker:text-primary",
        block.style === "number" ? "list-decimal marker:font-semibold" : "list-disc",
      )}>
      {items.map((item, index) => (
        <li key={index} className="pl-1">
          <InlineText text={item} />
        </li>
      ))}
    </List>
  );
};

const ImageView = ({ block, locale }: { block: ImageBlock; locale: Locale }): ReactNode => {
  const src = block.src.trim();
  if (!src) return null;
  const caption = pickText(block.caption, locale).trim();
  return (
    <figure>
      <img
        src={src}
        alt={pickText(block.alt, locale)}
        loading="lazy"
        decoding="async"
        className="h-auto w-full rounded-2xl bg-muted"
      />
      {caption && (
        <figcaption className="mt-2 text-base text-muted-foreground">
          <InlineText text={caption} />
        </figcaption>
      )}
    </figure>
  );
};

const calloutStyle: Record<CalloutTone, { icon: LucideIcon; box: string; accent: string }> = {
  tip: { icon: Lightbulb, box: "border-success/35 bg-success/8", accent: "text-success" },
  note: { icon: Info, box: "border-primary/30 bg-muted", accent: "text-primary" },
  warning: { icon: TriangleAlert, box: "border-warning/40 bg-warning/8", accent: "text-warning" },
};

const CalloutView = ({ block, locale }: { block: CalloutBlock; locale: Locale }): ReactNode => {
  const toneLabel = useSiteCopy().blocks.callout[block.tone];
  const title = pickText(block.title, locale).trim();
  const text = pickText(block.text, locale);
  if (!title && !text.trim()) return null;
  const { icon: Icon, box, accent } = calloutStyle[block.tone];
  return (
    <aside
      aria-label={title || toneLabel}
      className={cn("flex gap-3 rounded-2xl border p-4 sm:p-5", box)}>
      <Icon aria-hidden="true" className={cn("mt-1 size-6 shrink-0", accent)} />
      <div className="min-w-0 flex-1">
        <p className={cn("font-semibold", accent)}>{title || toneLabel}</p>
        {text.trim() && (
          <div className="mt-1 [&>p+p]:mt-3">
            <Paragraphs text={text} />
          </div>
        )}
      </div>
    </aside>
  );
};

const QuoteView = ({ block, locale }: { block: QuoteBlock; locale: Locale }): ReactNode => {
  const text = pickText(block.text, locale);
  if (!text.trim()) return null;
  const cite = pickText(block.cite, locale).trim();
  return (
    <figure className="border-l-4 border-accent pl-5">
      <blockquote className="font-display text-2xl leading-snug italic [&>p+p]:mt-3">
        <Paragraphs text={text} />
      </blockquote>
      {cite && <figcaption className="mt-3 text-base text-muted-foreground">— {cite}</figcaption>}
    </figure>
  );
};

const VideoView = ({ block, locale }: { block: VideoBlock; locale: Locale }): ReactNode => {
  const copy = useSiteCopy().blocks;
  const caption = pickText(block.caption, locale).trim();
  const embed = videoEmbedUrl(block.url);
  if (embed) {
    return (
      <figure>
        <div className="aspect-video overflow-hidden rounded-2xl bg-header">
          <iframe
            src={embed}
            title={caption || copy.video}
            loading="lazy"
            allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            className="size-full border-0"
          />
        </div>
        {caption && (
          <figcaption className="mt-2 text-base text-muted-foreground">{caption}</figcaption>
        )}
      </figure>
    );
  }
  const href = safeHref(block.url);
  if (!href) return null;
  return (
    <p>
      <a href={href} target="_blank" rel="noopener noreferrer">
        {caption || copy.watchVideo}
        <span className="sr-only"> {copy.opensInNewTab}</span>
      </a>
    </p>
  );
};

const LinkView = ({ block, locale }: { block: LinkBlock; locale: Locale }): ReactNode => {
  const copy = useSiteCopy().blocks;
  const href = safeHref(block.url);
  if (!href) return null;
  const external = !href.startsWith("/");
  const label = pickText(block.label, locale).trim() || href;
  const description = pickText(block.description, locale).trim();
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className={cn(
        "group flex items-start gap-4 rounded-2xl border border-border bg-card p-4 no-underline",
        "transition-colors hover:border-primary sm:p-5",
      )}>
      <span className="min-w-0 flex-1">
        <span className="block font-semibold text-primary group-hover:underline">{label}</span>
        {description && <span className="mt-1 block text-muted-foreground">{description}</span>}
        {external && <span className="sr-only"> {copy.opensInNewTab}</span>}
      </span>
      {external ? (
        <ExternalLink aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-primary" />
      ) : (
        <ArrowRight aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-primary" />
      )}
    </a>
  );
};
